import { useOrg } from '@/organization/useOrg';
import { AttachMoney, Cloud, Info } from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IconWrapper } from '../../components/icon/IconWrapper.tsx';
import { PaperTooltip } from '../../components/PaperTooltip.tsx';
import {
  BuildingAlternative,
  SubprojectBuildingDetails,
} from '../../stores/SubprojectStore.ts';
import { theme } from '../../theme/theme.ts';
import { formattedUSD, safeFormatDecimal } from '../../utils.ts';
import {
  useElectricRatesQuery,
  useGasRatesQuery,
  useOnsiteEnergyQuery,
} from '../queries/energy';
import { EnergyRateCard, ExternalEnergy, OnsiteEnergy } from '../types';
import { createEmptyBuildingAlternative, storedEnergyCosts } from '../utils.ts';
import { EnergyAvailableListing } from './EnergyAvailableListing.tsx';
import { EnergySelectedCards } from './EnergySelectedCards.tsx';
import { EnergySupplyCharts } from './EnergySupplyCharts.tsx';
import {
  adaptElectric,
  adaptGas,
  computeExternalMinMax,
  computeOnsiteMinMax,
  getAnnualEmissions,
} from './energyUtils';

// ---------------------------------------------------------------------------
// EnergyOverviewHeader (small, kept inline)
// ---------------------------------------------------------------------------

interface EnergyOverviewHeaderProps {
  // Stored backend figures (storedEnergyCosts); undefined renders '--'.
  capitalCost?: number;
  annualCost?: number;
  annualEmissions: number;
  hasEnergySelected: boolean;
}

const EnergyOverviewHeader: React.FC<EnergyOverviewHeaderProps> = ({
  capitalCost,
  annualCost,
  annualEmissions,
  hasEnergySelected,
}) => (
  <Stack direction={'row'} spacing={0.5} flex={1} sx={{ overflow: 'scroll' }}>
    <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
      <Stack>
        <Stack
          direction={'row'}
          spacing={1}
          alignItems={'center'}
          minHeight={30}
        >
          <AttachMoney fontSize={'small'} color={'info'} />
          <Typography
            variant={'subtitle2'}
            fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
          >
            CAPITAL COST
          </Typography>
        </Stack>
        <Box>
          <Typography
            fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
            variant={'subtitle2'}
          >
            {hasEnergySelected && capitalCost !== undefined
              ? formattedUSD(capitalCost)
              : '--'}
          </Typography>
        </Box>{' '}
      </Stack>
    </Stack>
    <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
      <Stack>
        <Stack
          direction={'row'}
          spacing={1}
          alignItems={'center'}
          minHeight={30}
        >
          <IconWrapper icon={'AnnualCost'} color={'info'} />
          <Typography
            variant={'subtitle2'}
            fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
          >
            ANNUAL COST
          </Typography>
        </Stack>
        <Box>
          <Typography
            fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
            variant={'subtitle2'}
          >
            {hasEnergySelected && annualCost !== undefined
              ? formattedUSD(annualCost)
              : '--'}
          </Typography>
        </Box>{' '}
      </Stack>
    </Stack>
    <Stack className={'overview-header-gradient'} flex={1} py={2} px={4}>
      <Stack>
        <Stack
          direction={'row'}
          spacing={1}
          alignItems={'center'}
          minHeight={30}
        >
          <Cloud fontSize={'small'} color={'info'} />
          <Typography
            variant={'subtitle2'}
            fontSize={{ sm: 4, md: 6, lg: 9, xl: 12 }}
          >
            ANNUAL EMISSIONS
          </Typography>
          <PaperTooltip
            title={
              <Stack spacing={0}>
                <Typography fontWeight={'bold'}>
                  Annual Greenhouse Gas (GHG) Emissions
                </Typography>{' '}
                <Typography>
                  shows how much CO2 will be produced by your selected energy
                  producers over the course of the year in order to meet your
                  energy needs.
                </Typography>
              </Stack>
            }
          >
            <Info color={'success'} fontSize={'small'} />
          </PaperTooltip>
        </Stack>
        <Box>
          <Typography
            fontSize={{ sm: 16, md: 18, lg: 20, xl: 24 }}
            variant={'subtitle2'}
          >
            {hasEnergySelected ? (
              <>
                {safeFormatDecimal(
                  annualEmissions,
                  'EnergyOverview.annualEmissions',
                )}{' '}
                tCO<sub>2</sub>e
              </>
            ) : (
              '--'
            )}
          </Typography>
        </Box>{' '}
      </Stack>
    </Stack>
  </Stack>
);

// ---------------------------------------------------------------------------
// Main component (exported)
// ---------------------------------------------------------------------------

interface EnergyForBuildingDetailsProps {
  building: SubprojectBuildingDetails;
  alternative: BuildingAlternative;
  onAddOnsiteEnergy: (techId: string) => void;
  onAddExternalEnergy: (card: EnergyRateCard) => void;
  onRemoveOnsiteEnergy: (techId: string) => void;
  onRemoveExternalEnergy: (key: string) => void;
  onChangeOnsiteEnergyCount: (techId: string, newCount: number) => void;
  emptyText: React.ReactNode;
  readOnly?: boolean;
  // The stored cost figures predate the energy selections on screen.
  costsArePending?: boolean;
}

export const EnergyForBuildingDetails: React.FC<
  EnergyForBuildingDetailsProps
> = ({
  building,
  alternative,
  onAddOnsiteEnergy,
  onAddExternalEnergy,
  onRemoveExternalEnergy,
  onChangeOnsiteEnergyCount,
  emptyText,
  readOnly,
  costsArePending = false,
}) => {
  const { selectedOrg } = useOrg();
  const { projectId } = useParams();
  const orgId = selectedOrg?.id;
  const subprojectId = building.id;

  const { data: onsite = [], isLoading: onsiteLoading } =
    useOnsiteEnergyQuery();
  const { data: electricRates = [], isLoading: electricLoading } =
    useElectricRatesQuery(orgId, projectId, subprojectId);
  const { data: gasRates = [], isLoading: gasLoading } = useGasRatesQuery(
    orgId,
    projectId,
    subprojectId,
  );

  const referenceDataLoading = onsiteLoading || electricLoading || gasLoading;

  // All adapted rate cards — used for minMax, cost overview, and selected-card lookup
  const allAdaptedExternal = useMemo<ExternalEnergy[]>(
    () => [...electricRates.map(adaptElectric), ...gasRates.map(adaptGas)],
    [electricRates, gasRates],
  );

  const minMax = useMemo(
    () => ({
      onsite: computeOnsiteMinMax(onsite),
      external: computeExternalMinMax(allAdaptedExternal),
    }),
    [onsite, allAdaptedExternal],
  );

  const findOnsite = useCallback(
    (id: string) => onsite.find((e) => e.id === id),
    [onsite],
  );

  // findExternal looks up by natural key `utilityIdentifier:ratePlanType:state:sector`
  // and returns an ExternalEnergy-shaped adapter for display components
  const findExternal = useCallback(
    (key: string): ExternalEnergy | undefined =>
      allAdaptedExternal.find((e) => e.id === key),
    [allAdaptedExternal],
  );

  const totalElectricityUsePerYear =
    alternative?.energyDemand?.Electricity || 0;

  // Costs come from the stored backend figures, never local arithmetic -- see
  // storedEnergyCosts. The tab badge (EnergySelection) reads the same capital.
  const { capitalCost, annualCost } = storedEnergyCosts(
    alternative,
    costsArePending,
  );
  const annualEmissions = getAnnualEmissions(
    onsite,
    allAdaptedExternal,
    alternative || createEmptyBuildingAlternative(),
  );

  const hasEnergySelected = Boolean(
    alternative?.onSiteEnergy?.params?.length ||
    alternative?.externalEnergy?.params?.length,
  );

  const onsiteProviders = useMemo(() => {
    if (!alternative?.onSiteEnergy?.params) return [];
    return alternative.onSiteEnergy.params.reduce<OnsiteEnergy[]>((acc, v) => {
      const unit = findOnsite(v.techId);
      if (unit) acc.push(unit);
      return acc;
    }, []);
  }, [findOnsite, alternative]);

  // Build the selected external providers list for EnergySupplyCharts
  const externalProviders = useMemo(() => {
    if (!alternative?.externalEnergy?.params) return [];
    return alternative.externalEnergy.params.reduce<ExternalEnergy[]>(
      (acc, v) => {
        const key = `${v.utilityIdentifier}:${v.ratePlanType}:${v.state}:${v.sector}`;
        const unit = findExternal(key);
        if (unit) acc.push(unit);
        return acc;
      },
      [],
    );
  }, [findExternal, alternative]);

  if (!alternative) {
    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.primary.main,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'stretch',
          m: [0, 2, 0, 2],
          p: 2,
        }}
      >
        <Typography variant='h6' color='text.secondary'>
          Loading energy configuration...
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: theme.palette.primary.main,
        display: 'grid',
        gridTemplateAreas: '"listing selected"',
        gridTemplateColumns: '0.57fr 0.43fr',
        gap: 1,
        height: 'stretch',
        m: [0, 2, 0, 2],
        p: 2,
      }}
    >
      <Box
        sx={{
          gridArea: 'selected',
          display: 'grid',
          overflow: 'hidden',
          gridTemplateAreas: '"demand" "usage" "components" "adjustments"',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'auto auto 1fr auto',
          p: 2,
        }}
      >
        <EnergyOverviewHeader
          capitalCost={capitalCost}
          annualCost={annualCost}
          annualEmissions={annualEmissions}
          hasEnergySelected={hasEnergySelected}
        />
        <EnergySupplyCharts
          alternative={alternative}
          onsiteProviders={onsiteProviders}
          externalProviders={externalProviders}
          findOnsite={findOnsite}
        />
        <EnergySelectedCards
          alternative={alternative}
          minMax={minMax}
          totalElectricityUsePerYear={totalElectricityUsePerYear}
          findOnsite={findOnsite}
          findExternal={findExternal}
          onChangeOnsiteEnergyCount={onChangeOnsiteEnergyCount}
          onRemoveExternalEnergy={onRemoveExternalEnergy}
          emptyText={emptyText}
          referenceDataLoading={referenceDataLoading}
          readOnly={readOnly}
        />
      </Box>
      {readOnly ? (
        <Box sx={{ gridArea: 'listing' }} />
      ) : (
        <EnergyAvailableListing
          onsite={onsite}
          electricRates={electricRates}
          gasRates={gasRates}
          alternative={alternative}
          minMax={minMax}
          totalElectricityUsePerYear={totalElectricityUsePerYear}
          onAddOnsiteEnergy={onAddOnsiteEnergy}
          onAddExternalEnergy={onAddExternalEnergy}
        />
      )}
    </Paper>
  );
};
